import { useCallback, useMemo, useState } from 'react';
import type { ColetaDaLista, FiltroDeColetas, SituacaoColeta } from '../api/coletas.js';
import { listarColetas, rotuloDaOrigem, situacaoDaColeta } from '../api/coletas.js';
import { listarOperadores } from '../api/operadores.js';
import {
  contagemDoRecorte,
  intervaloDaPagina,
  TAMANHO_PAGINA_MAXIMO,
  totalDePaginas,
} from '../api/paginacao.js';
import { seInformado } from '../api/recorte.js';
import { useConsulta } from '../api/useConsulta.js';
import { Botao } from '../componentes/Botao.js';
import { ChipStatus } from '../componentes/ChipStatus.js';
import type { TomStatus } from '../componentes/ChipStatus.js';
import { formatarHora } from '../componentes/datas.js';
import { EstadoVazio } from '../componentes/EstadoVazio.js';
import {
  CampoBusca,
  ContadorRegistros,
  FiltroAtivo,
  FiltroSelecao,
} from '../componentes/Filtros.js';
import type { OpcaoFiltro } from '../componentes/Filtros.js';
import { IconeColetas, IconeExportar, IconeMais } from '../componentes/Icones.js';
import { Pagina } from '../componentes/Pagina.js';
import { Paginacao } from '../componentes/Paginacao.js';
import { Resultado } from '../componentes/Resultado.js';
import { Tabela } from '../componentes/Tabela.js';
import type { Coluna } from '../componentes/Tabela.js';
import { BacklogDoPedido } from './coletas/BacklogDoPedido.js';
import { NovoQrDePedido } from './coletas/NovoQrDePedido.js';
import { useValorAdiado } from './movimentacoes/useValorAdiado.js';
import { inicioDoPeriodo, PERIODO_PADRAO, PERIODOS } from './movimentacoes/formato.js';
import './Coletas.css';

const TODOS = 'todos';

/** Rótulo e tom de cada situação — cor sozinha nunca comunica estado (RNF-022). */
const CHIP_COLETA: Readonly<Record<SituacaoColeta, { tom: TomStatus; rotulo: string }>> = {
  andamento: { tom: 'andamento', rotulo: 'Em andamento' },
  concluida: { tom: 'concluida', rotulo: 'Concluída' },
  divergencia: { tom: 'divergencia', rotulo: 'Com divergência' },
  cancelada: { tom: 'desativado', rotulo: 'Cancelada' },
};

/**
 * Duração entre a primeira e a última execução, em minutos cheios.
 *
 * Documento sem execução não tem duração — e "0m" seria pior do que o travessão:
 * afirmaria uma coleta instantânea onde ninguém coletou nada ainda.
 */
function duracaoDaColeta(coleta: ColetaDaLista): string {
  if (coleta.iniciadaEm === null || coleta.encerradaEm === null) {
    return '—';
  }

  const minutos = Math.round(
    (new Date(coleta.encerradaEm).getTime() - new Date(coleta.iniciadaEm).getTime()) / 60000,
  );

  return minutos < 60
    ? `${String(minutos)}m`
    : `${String(Math.floor(minutos / 60))}h ${String(minutos % 60)}m`;
}

function montarColunas(aoAbrir: (coleta: ColetaDaLista) => void): readonly Coluna<ColetaDaLista>[] {
  return [
    {
      chave: 'pedido',
      rotulo: 'Pedido',
      largura: '22%',
      // O número é BOTÃO, e não a linha inteira: a linha vai ganhar outras ações
      // (exportar, reprocessar), e alvo de clique que cobre tudo rouba as demais.
      celula: (coleta) => (
        <span className="coletas__pedido">
          <button
            type="button"
            className="coletas__pedido-botao mono"
            onClick={() => {
              aoAbrir(coleta);
            }}
          >
            {coleta.pedido}
          </button>
          <span className="coletas__origem-doc">{rotuloDaOrigem(coleta.origem)}</span>
        </span>
      ),
    },
    {
      chave: 'operador',
      rotulo: 'Operador',
      largura: '20%',
      // Nulo enquanto ninguém executou nada: o documento nasce da primeira linha
      // que chega da Fila A, e até lá não há autor a exibir.
      celula: (coleta) => coleta.operador?.nome ?? '—',
    },
    {
      chave: 'itens',
      rotulo: 'Itens',
      largura: '10%',
      celula: (coleta) => `${String(coleta.itensConcluidos)} / ${String(coleta.totalItens)}`,
    },
    {
      chave: 'inicio',
      rotulo: 'Início',
      largura: '10%',
      // Horário do APARELHO (RF-052) — é ele que diz quando a coleta aconteceu.
      celula: (coleta) => formatarHora(coleta.iniciadaEm),
    },
    {
      chave: 'duracao',
      rotulo: 'Duração',
      largura: '10%',
      celula: duracaoDaColeta,
    },
    {
      chave: 'status',
      rotulo: 'Status',
      largura: '16%',
      celula: (coleta) => {
        const chip = CHIP_COLETA[situacaoDaColeta(coleta)];
        return <ChipStatus tom={chip.tom} rotulo={chip.rotulo} tamanho="tabela" />;
      },
    },
    {
      chave: 'vinculos',
      rotulo: 'Vínculos manuais',
      largura: '12%',
      alinhamento: 'direita',
      // RF-020 — a coluna existe porque vínculo manual é o sinal de codificação
      // divergente entre o ERP e a etiqueta do fornecedor.
      celula: (coleta) => (coleta.vinculosManuais === 0 ? '—' : String(coleta.vinculosManuais)),
    },
  ];
}

/**
 * Pedidos coletados (UC10, RF-070) — a listagem mais usada do painel.
 *
 * Lê do middleware, com paginação de SERVIDOR: o contador da faixa é o total do
 * filtro, e não o tamanho da página. Enquanto esta tela vinha de
 * `dados-exemplo`, ela anunciava 1.284 registros e pedidos que nunca
 * existiram — e quem coletava no coletor não encontrava a própria coleta aqui.
 */
export function Coletas() {
  const [busca, definirBusca] = useState('');
  const [operadorId, definirOperadorId] = useState(TODOS);
  const [periodo, definirPeriodo] = useState(PERIODO_PADRAO);
  const [somenteDivergencia, definirSomenteDivergencia] = useState(false);
  const [gerandoQr, definirGerandoQr] = useState(false);
  const [aberto, definirAberto] = useState<ColetaDaLista | null>(null);
  const [pagina, definirPagina] = useState(1);

  const buscaAdiada = useValorAdiado(busca);

  const filtro = useMemo<FiltroDeColetas>(
    () => ({
      pagina,
      ...seInformado('de', inicioDoPeriodo(periodo)),
      ...seInformado('referencia', buscaAdiada),
      ...seInformado('operadorId', operadorId === TODOS ? null : operadorId),
    }),
    [pagina, periodo, buscaAdiada, operadorId],
  );

  const colunas = useMemo(() => montarColunas(definirAberto), []);
  const buscar = useCallback(() => listarColetas(filtro), [filtro]);
  const consulta = useConsulta(buscar);
  const opcoesDeOperador = useOpcoesDeOperador();

  /** Trocar o recorte volta para a primeira página: a 7 do filtro antigo não existe no novo. */
  function recortar(aplicar: () => void) {
    aplicar();
    definirPagina(1);
  }

  return (
    <Pagina
      contexto="Coletas"
      titulo="Pedidos coletados"
      acoes={
        <>
          <Botao altura="media">
            <IconeExportar tamanho={15} />
            Exportar CSV
          </Botao>
          {/*
            "Novo QR de pedido", e não "Nova coleta": a coleta nasce no coletor,
            quando o operador lê o código (UC02). O que o painel faz é IMPRIMIR
            o pedido que ele vai ler.
          */}
          <Botao
            altura="media"
            variante="primario"
            onClick={() => {
              definirGerandoQr(true);
            }}
          >
            <IconeMais tamanho={15} />
            Novo QR de pedido
          </Botao>
        </>
      }
      filtros={
        <>
          <CampoBusca
            rotulo="Buscar por número do pedido"
            valor={busca}
            aoMudar={(valor) => {
              recortar(() => {
                definirBusca(valor);
              });
            }}
          />
          <FiltroSelecao
            rotulo="Período"
            valor={periodo}
            opcoes={PERIODOS}
            aoMudar={(valor) => {
              recortar(() => {
                definirPeriodo(valor);
              });
            }}
          />
          <FiltroSelecao
            rotulo="Operador"
            valor={operadorId}
            opcoes={opcoesDeOperador}
            aoMudar={(valor) => {
              recortar(() => {
                definirOperadorId(valor);
              });
            }}
          />
          {/*
            Filtro do CLIENTE, e só ele: a divergência é derivada das execuções
            do documento, e o middleware ainda não a recorta. Fica explícito
            aqui para ninguém confundir com os filtros que vão ao servidor —
            este só alcança a página carregada.
          */}
          <FiltroAtivo
            rotulo="Com divergência (nesta página)"
            ativo={somenteDivergencia}
            aoAlternar={() => {
              definirSomenteDivergencia((ligado) => !ligado);
            }}
          />
          {consulta.dados !== null && (
            <ContadorRegistros>
              {contagemDoRecorte(consulta.dados.total, 'coleta', 'coletas')}
            </ContadorRegistros>
          )}
        </>
      }
    >
      <Resultado
        consulta={consulta}
        espera="Carregando coletas…"
        estaVazio={(dados) => dados.itens.length === 0}
        vazio={
          <EstadoVazio
            tom="neutro"
            icone={<IconeColetas tamanho={28} />}
            titulo="Nenhuma coleta neste recorte."
            detalhe="Amplie o período ou limpe os filtros para ver mais pedidos."
          />
        }
      >
        {(dados) => {
          const visiveis = somenteDivergencia
            ? dados.itens.filter((coleta) => situacaoDaColeta(coleta) === 'divergencia')
            : dados.itens;

          return (
            <Tabela
              itens={visiveis}
              colunas={colunas}
              chaveDe={(coleta) => coleta.id}
              descricao="Pedidos coletados"
              rodape={
                <Paginacao
                  intervalo={intervaloDaPagina(dados, 'coleta', 'coletas')}
                  pagina={dados.pagina}
                  totalPaginas={totalDePaginas(dados)}
                  aoIrPara={definirPagina}
                />
              }
            />
          );
        }}
      </Resultado>

      {gerandoQr && (
        <NovoQrDePedido
          aoFechar={() => {
            definirGerandoQr(false);
          }}
        />
      )}

      {aberto !== null && (
        <BacklogDoPedido
          id={aberto.id}
          pedido={aberto.pedido}
          aoFechar={() => {
            definirAberto(null);
          }}
        />
      )}
    </Pagina>
  );
}

/**
 * Operadores do tenant para o filtro por autor.
 *
 * Uma página só, e grande: o filtro é um seletor, não uma listagem. Falha ao
 * carregar não derruba a tela — mas também não passa em branco: o rótulo diz
 * que a lista não veio, senão um seletor com uma opção só pareceria um armazém
 * sem nenhum operador cadastrado.
 */
function useOpcoesDeOperador(): readonly OpcaoFiltro[] {
  const buscar = useCallback(() => listarOperadores({ tamanho: TAMANHO_PAGINA_MAXIMO }), []);
  const { dados, erro } = useConsulta(buscar);

  return useMemo(
    () => [
      {
        valor: TODOS,
        rotulo: erro === null ? 'Operador · todos' : 'Operador · lista indisponível',
      },
      ...(dados?.itens ?? []).map((operador) => ({
        valor: operador.id,
        rotulo: `${operador.nome} · ${operador.matricula}`,
      })),
    ],
    [dados, erro],
  );
}
